import type { GeneratedRoof, RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofEdge } from "building-types/roofs/roofEdgeTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Wall } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import { isInteriorRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { isExteriorWallCondition } from "doc/utils/wallTypeTools";
import { aabb3ByTwoPoints } from "geom/aabb3";
import type { Face } from "geom/brep";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ONE_MINUS_DIST_EPSILON } from "geom/core";
import { joinPolyLine3Rails } from "geom/join";
import {
  planeCopy,
  planeCopyReversed,
  planeIsCoplanar,
  planeIsParallel,
  planeMoveAlongNormal,
  planeNormal,
  planeProjectPolyLine3,
  planeSignedDistanceTo,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2Area, polyLine2Concat, polyLine2Length } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Copy, polyLine3EndPoint, polyLine3Move, polyLine3OnPlane, polyLine3StartPoint } from "geom/polyLine3";
import { trimRailWithClosedPolyLine } from "geom/trim";
import { vec2DistSquared } from "geom/vec2";
import { vec3AngleBetween, vec3Dot } from "geom/vec3";
import type { RBush3D } from "rbush-3d";
import { getWallPath } from "../walls/wall";

const MINIMUM_REPROJECTION_OFFSET = 0.01;
const BACK_PLANE_TOLERANCE = 0.01;

export interface CoplanarRails {
  readonly plane: Plane;
  readonly projDirectionX: number;
  readonly projDirectionY: number;
  readonly projDirectionZ: number;
  readonly rails: PolyLine3[];
  readonly railLateralOffsetFromBasePlane: number;
}

interface GableRoofRailStrip {
  rail: PolyLine2;
  edges: RoofEdge[];
  brokenStart: boolean;
  brokenEnd: boolean;
}

export function coplanarRailsNew(
  plane: Plane,
  projDirectionX: number,
  projDirectionY: number,
  projDirectionZ: number,
  rails: PolyLine3[] = [],
  railLateralOffsetFromBasePlane: number = 0,
): CoplanarRails {
  return {
    plane,
    projDirectionX,
    projDirectionY,
    projDirectionZ,
    rails,
    railLateralOffsetFromBasePlane,
  };
}

function gableRoofRailStripNew(
  rail: PolyLine2,
  edges: RoofEdge[] = [],
  brokenStart: boolean = false,
  brokenEnd: boolean = false,
): GableRoofRailStrip {
  return { rail, edges, brokenStart, brokenEnd };
}

export function groupCoplanarRails(coplanarRails: CoplanarRails[]): CoplanarRails[] {
  const groupedCoplanarRails: CoplanarRails[] = [];

  for (const {
    plane: inputPlane,
    rails: inputRails,
    projDirectionX: inputDirX,
    projDirectionY: inputDirY,
    projDirectionZ: inputDirZ,
  } of coplanarRails) {
    for (const inputRail of inputRails) {
      let found = false;
      for (const {
        plane: groupedPlane,
        rails: groupedRails,
        projDirectionX: groupedDirX,
        projDirectionY: groupedDirY,
        projDirectionZ: groupedDirZ,
      } of groupedCoplanarRails) {
        if (
          planeIsCoplanar(inputPlane, groupedPlane) &&
          vec3Dot(inputDirX, inputDirY, inputDirZ, groupedDirX, groupedDirY, groupedDirZ) > ONE_MINUS_DIST_EPSILON
        ) {
          groupedRails.push(inputRail);
          found = true;
          break;
        }
      }

      if (!found) {
        groupedCoplanarRails.push(coplanarRailsNew(inputPlane, inputDirX, inputDirY, inputDirZ, [inputRail]));
      }
    }
  }

  return groupedCoplanarRails;
}

interface RejoinCoplanarRailsOptions {
  readonly inputRails: CoplanarRails[];
  readonly rtree?: RBush3D;
  readonly boxHalfSize?: number;
  readonly keepRailsDirection?: boolean;
  readonly searchBoxTolerance?: number;
  readonly joinRailTolerance?: number;
}

export function rejoinCoplanarRails({
  inputRails,
  rtree = undefined,
  boxHalfSize = 0.1,
  keepRailsDirection = false,
  searchBoxTolerance = MULTIGABLE_BSP_EPSILON,
  joinRailTolerance = MULTIGABLE_BSP_EPSILON,
}: RejoinCoplanarRailsOptions): CoplanarRails[] {
  let index = 0;

  // join rails from the same group and create a rtree with the resulting rail ends
  const coplanarJoinedRails: CoplanarRails[] = [];
  for (const { rails, plane, projDirectionX, projDirectionY, projDirectionZ } of inputRails) {
    // join the sub-rails
    const joinedRails = joinPolyLine3Rails({
      segments: rails,
      searchBoxTolerance,
      angleTolerance: MULTIGABLE_BSP_EPSILON,
      simplify: true,
      allowRevert: !keepRailsDirection,
      toleranceSq: joinRailTolerance * joinRailTolerance,
    });

    // insert the end points into the rtree
    if (rtree) {
      for (const joinedRail of joinedRails) {
        const [startX, startY, startZ] = polyLine3StartPoint(joinedRail);
        const [endX, endY, endZ] = polyLine3EndPoint(joinedRail);

        const aabb1 = aabb3ByTwoPoints(
          startX - boxHalfSize,
          startY - boxHalfSize,
          startZ - boxHalfSize,
          startX + boxHalfSize,
          startY + boxHalfSize,
          startZ + boxHalfSize,
        );

        rtree.insert({
          ...aabb1,
          index,
        });

        const aabb2 = aabb3ByTwoPoints(
          endX - boxHalfSize,
          endY - boxHalfSize,
          endZ - boxHalfSize,
          endX + boxHalfSize,
          endY + boxHalfSize,
          endZ + boxHalfSize,
        );

        rtree.insert({
          ...aabb2,
          index,
        });

        index++;
      }
    }

    coplanarJoinedRails.push(coplanarRailsNew(plane, projDirectionX, projDirectionY, projDirectionZ, joinedRails));
  }

  return coplanarJoinedRails;
}

export function railStripsByPredicate(
  perimeter: PolyLine2,
  edgesInfo: RoofEdge[],
  predicate: (edgesInfo: RoofEdge[], edgeIndex: number) => boolean,
  continuationPredicate: ((edgesInfo: RoofEdge[], edgeIndex: number) => boolean) | null = null,
): GableRoofRailStrip[] {
  const strips: GableRoofRailStrip[] = [];

  if (!(perimeter.length / 2 - 1 === edgesInfo.length)) {
    console.assert(false);
  }

  let curStrip: GableRoofRailStrip | null = null;

  for (let i = 0, l = edgesInfo.length; i < l; i++) {
    const i2 = i * 2;
    const nextI2 = i2 + 2;
    const prevI = i === 0 ? edgesInfo.length - 1 : i - 1;
    const predicateVal = predicate ? predicate(edgesInfo, i) : true;
    const prevPredicateVal = predicate ? predicate(edgesInfo, prevI) : true;
    const continuationPredicateVal = continuationPredicate ? continuationPredicate(edgesInfo, i) : true;
    const brokenStart = prevPredicateVal && predicateVal && !continuationPredicateVal;

    if (curStrip && !continuationPredicateVal) {
      curStrip.brokenEnd = predicateVal;

      // add the current strip to the result list.
      strips.push(curStrip);

      // reset the current strip
      curStrip = null;
    }

    if (predicateVal) {
      // the strip may not be initialized yet
      if (!curStrip) {
        curStrip = gableRoofRailStripNew(polyLine2New([perimeter[i2], perimeter[i2 + 1]]), [], brokenStart);
      }

      curStrip.rail = polyLine2Concat(curStrip.rail, polyLine2New([perimeter[nextI2], perimeter[nextI2 + 1]]));
      curStrip.edges.push(edgesInfo[i]);

      // if we are at the end of the list
      // we terminate the strip
      if (i === l - 1) {
        const startContinuationPredicateVal = continuationPredicate ? continuationPredicate(edgesInfo, 0) : true;
        const startPredicateVal = predicate ? predicate(edgesInfo, 0) : true;
        curStrip.brokenEnd = !startContinuationPredicateVal && startPredicateVal;

        strips.push(curStrip);
      }
    }

    if (!predicateVal && curStrip) {
      // we have a non-empty strip, but the predicate is no longer
      // true. add the current strip to the result list.
      strips.push(curStrip);

      // reset the current strip
      curStrip = null;
    }
  }

  // if the last and first strip are connected, turn them into a single strip
  const continuationPredicatePredicateVal = continuationPredicate ? continuationPredicate(edgesInfo, 0) : true;
  if (strips.length > 1 && continuationPredicatePredicateVal) {
    const startStrip = strips[0];
    const endStrip = strips[strips.length - 1];

    if (startStrip !== endStrip) {
      const [firstX, firstY] = [startStrip.rail[0], startStrip.rail[1]];
      const [lastX, lastY] = [endStrip.rail[endStrip.rail.length - 2], endStrip.rail[endStrip.rail.length - 1]];

      if (vec2DistSquared(firstX, firstY, lastX, lastY) < DIST_EPSILON_SQUARED) {
        strips[0] = gableRoofRailStripNew(
          polyLine2Concat(endStrip.rail, startStrip.rail.slice(2)),
          [...endStrip.edges, ...startStrip.edges],
          endStrip.brokenStart,
          startStrip.brokenEnd,
        );

        strips.pop();
      }
    }
  }

  return strips;
}

interface ReprojectCoplanarRailsArrayToWallsOptions {
  readonly coplanarRailsArray: CoplanarRails[];
  readonly walls: Table<WallSystem>;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly projectionDirectionX?: number;
  readonly projectionDirectionY?: number;
  readonly projectionDirectionZ?: number;
  readonly reprojectedCoplanarRails?: CoplanarRails[];
  readonly extraWallLikeFaces?: Face[];
  readonly offsetZ?: number;
}

export function reprojectCoplanarRailsArrayToWalls({
  coplanarRailsArray,
  walls,
  subRoofs,
  reprojectedCoplanarRails = [],
  extraWallLikeFaces = [],
  offsetZ = 0,
}: ReprojectCoplanarRailsArrayToWallsOptions): CoplanarRails[] {
  for (const coplanarRails of coplanarRailsArray) {
    reprojectCoplanarRailsToWalls({
      coplanarRails,
      walls,
      subRoofs,
      reprojectedCoplanarRails,
      extraWallLikeFaces,
      offsetZ,
    });
  }
  return reprojectedCoplanarRails;
}

interface ReprojectCoplanarRailsToWallsOptions {
  readonly coplanarRails: CoplanarRails;
  readonly walls: Table<WallSystem>;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly reprojectedCoplanarRails?: CoplanarRails[];
  readonly extraWallLikeFaces?: Face[];
  readonly offsetZ?: number;
}

function reprojectCoplanarRailsToWalls({
  coplanarRails,
  walls,
  subRoofs,
  reprojectedCoplanarRails = [],
  extraWallLikeFaces = [],
  offsetZ = 0,
}: ReprojectCoplanarRailsToWallsOptions): CoplanarRails[] {
  const { plane, projDirectionX, projDirectionY, projDirectionZ, rails, railLateralOffsetFromBasePlane } =
    coplanarRails;
  const [nX, nY, nZ] = planeNormal(plane);
  const baseRailPlane =
    railLateralOffsetFromBasePlane !== 0
      ? planeMoveAlongNormal(planeCopy(plane), -railLateralOffsetFromBasePlane)
      : plane;
  const { originX, originY, originZ } = baseRailPlane;

  let availableRails = rails.map((x) => polyLine3Move(polyLine3Copy(x), 0, 0, offsetZ));
  const planeIsParallelAndBack = (refPlane: Plane, plane: Plane): boolean => {
    const { originX, originY, originZ } = plane;

    return (
      planeIsParallel(refPlane, plane) &&
      planeSignedDistanceTo(refPlane, originX, originY, originZ) < BACK_PLANE_TOLERANCE
    );
  };

  const rail2DToRail3D = (
    pl: PolyLine2,
    srcPlane: Plane,
    dstPlane: Plane,
    offsetX: number = 0,
    offsetY: number = 0,
    offsetZ: number = 0,
  ): PolyLine3 => {
    const p3 = polyLine3Move(polyLine3OnPlane(pl, srcPlane), offsetX, offsetY, offsetZ);
    return polyLine3OnPlane(planeProjectPolyLine3(dstPlane, p3), dstPlane);
  };

  const rail3DToRail2D = (
    pl: PolyLine3,
    dstPlane: Plane,
    offsetX: number = 0,
    offsetY: number = 0,
    offsetZ: number = 0,
  ): PolyLine2 => {
    return planeProjectPolyLine3(dstPlane, polyLine3Move(polyLine3Copy(pl), offsetX, offsetY, offsetZ));
  };

  const reprojectRailsToFaces = (oppPlane: Plane, exteriorFaces: Face[]) => {
    if (availableRails.length === 0) {
      return;
    }

    // check that we are on the correct side of the wall opposite face
    if (!planeIsParallelAndBack(oppPlane, plane)) {
      return;
    }

    for (const exteriorFace of exteriorFaces) {
      const { loops, plane: exteriorPlane } = exteriorFace;

      // check that we are on the correct side of the wall exterior face
      if (!planeIsParallelAndBack(exteriorPlane, baseRailPlane)) {
        continue;
      }

      // discard invalid loops
      if (loops.length === 0 || polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED) {
        continue;
      }

      // get the offset
      let offset = -planeSignedDistanceTo(exteriorPlane, originX, originY, originZ);

      // ignore small offsets for better numerical stability
      if (Math.abs(offset) < MINIMUM_REPROJECTION_OFFSET) {
        offset = 0;
      }

      const angle = vec3AngleBetween(projDirectionX, projDirectionY, projDirectionZ, nX, nY, nZ);
      const projOffset = offset / Math.cos(angle);
      const projOffsetX = projDirectionX * projOffset;
      const projOffsetY = projDirectionY * projOffset;
      const projOffsetZ = projDirectionZ * projOffset;

      // project the input rails onto the exterior face plane
      const availableRails2D = availableRails.map((rail) =>
        rail3DToRail2D(rail, exteriorPlane, projOffsetX, projOffsetY, projOffsetZ),
      );
      // trim the projected rails with the exterior face loops
      const insideRails2D: PolyLine2[] = [];
      const outsideRails2D: PolyLine2[] = [];

      for (const rail2D of availableRails2D) {
        trimRailWithClosedPolyLine(rail2D, loops[0], insideRails2D, outsideRails2D, DIST_EPSILON, DIST_EPSILON);
      }

      // convert the inside 2D rails back to 3d
      const insideRails = insideRails2D
        .filter((rail2D) => polyLine2Length(rail2D) > DIST_EPSILON)
        .map((rail2D) =>
          rail2DToRail3D(rail2D, exteriorPlane, plane, -projOffsetX, -projOffsetY, -projOffsetZ - offsetZ),
        );

      // convert the outside 2D rails back to 3d and set them as the available rails for the next iteration
      availableRails = outsideRails2D
        .filter((rail2D) => polyLine2Length(rail2D) > DIST_EPSILON)
        .map((rail2D) => rail2DToRail3D(rail2D, exteriorPlane, plane, -projOffsetX, -projOffsetY, -projOffsetZ));

      if (insideRails.length === 0) {
        continue;
      }

      const sectionRails = insideRails.map((rail) => polyLine3Move(rail, projOffsetX, projOffsetY, projOffsetZ));
      const sectionPlane = planeMoveAlongNormal(planeCopy(plane), offset);

      // add them to the result
      reprojectedCoplanarRails.push(
        coplanarRailsNew(sectionPlane, projDirectionX, projDirectionY, projDirectionZ, sectionRails),
      );
    }
  };

  // helper functions
  const reprojectRailsToWall = (wall: Wall) => {
    if (wall.wallType !== WallType.SOLID) {
      return;
    }

    if (!isExteriorWallCondition[wall.wallCondition]) {
      return;
    }

    const { wallFaces, mass } = wall;
    const path = getWallPath(wall);
    const { leftFace, rightFace } = mass;
    const { leftRoom, rightRoom } = path;
    const { leftFaces, rightFaces, startFaces, endFaces } = wallFaces;

    if (leftRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[leftRoom?.attributes.roomEnclosure]) {
      reprojectRailsToFaces(leftFace.plane, rightFaces);

      if (startFaces.length > 0) {
        reprojectRailsToFaces(planeCopyReversed(startFaces[0].plane), startFaces);
      }

      if (endFaces.length > 0) {
        reprojectRailsToFaces(planeCopyReversed(endFaces[0].plane), endFaces);
      }
    }

    if (rightRoom?.attributes.roomEnclosure && isInteriorRoomEnclosure[rightRoom?.attributes.roomEnclosure]) {
      reprojectRailsToFaces(rightFace.plane, leftFaces);

      if (startFaces.length > 0) {
        reprojectRailsToFaces(planeCopyReversed(startFaces[0].plane), startFaces);
      }

      if (endFaces.length > 0) {
        reprojectRailsToFaces(planeCopyReversed(endFaces[0].plane), endFaces);
      }
    }
  };

  const reprojectRailsToWallSystem = ({ walls }: WallSystem) => {
    for (const wallId in walls) {
      reprojectRailsToWall(walls[wallId]);
    }
  };

  const reprojectRailsToRoofWallFaces = (roofWallFaces: RoofWallFaces) => {
    const { srcFace, leftFaces, thickness } = roofWallFaces;
    if (!srcFace) {
      return;
    }

    const oppPlane = planeMoveAlongNormal(planeCopyReversed(srcFace.plane), thickness);
    reprojectRailsToFaces(oppPlane, leftFaces);
  };

  for (const wallSystemId in walls) {
    reprojectRailsToWallSystem(walls[wallSystemId]);
  }

  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];
    const { roofWallSystem } = subRoof;
    if (roofWallSystem) {
      reprojectRailsToWallSystem(roofWallSystem.wallSystem);
    } else {
      const { wallFaces } = subRoof;
      const { startWallFaces, endWallFaces } = wallFaces;

      // old code path
      reprojectRailsToRoofWallFaces(startWallFaces);
      reprojectRailsToRoofWallFaces(endWallFaces);
    }
  }

  for (const extraWallLikeFace of extraWallLikeFaces) {
    reprojectRailsToFaces(planeCopyReversed(extraWallLikeFace.plane), [extraWallLikeFace]);
  }

  return reprojectedCoplanarRails;
}
